// Kayıt Defteri veri erişimi. Karşılıkları: src/shadow/api/app.py (/api/ledger/*)
//
// Bu uçlar motorun belleğinden değil doğrudan Postgres'ten okur: kayıt
// defterinin amacı geçmişi incelemek ve geçmiş, bellekteki pencereden büyük.

import type { OrderStatus, Side } from './types'

export interface LedgerOrder {
  id: string
  contract: string
  side: Side
  price: number
  quantity: number
  filledQuantity: number
  remaining: number
  status: OrderStatus
  version: number
  queueAhead: number | null
  createdAt: number
  updatedAt: number
  /** Dolum kırılımı: kaynak → lot — özet metni için `dolumOzeti()`. */
  fillBreakdown?: Record<string, number>
}

export interface LedgerFill {
  id: number
  orderId: string
  contract: string
  side: Side
  price: number
  quantity: number
  tradeId: string
  tradePrice: number
  ts: number
  /** Dolumun kaynağı: trade=Simüle, book=Süpürüldük, sweep=Süpürdük. */
  liquidity?: 'trade' | 'book' | 'sweep'
  orderVersion: number | null
  remainingAfter: number | null
  /** Dolum tutarı, TL — fiyat (TL/MWh) × lot × 0,1. */
  notional: number | null
  priceAdvantage: number | null
  /** Dolum anındaki tahta — başka hiçbir yerde saklanmıyor. */
  bestBid: number | null
  bestAsk: number | null
  mcp: number | null
}

export interface OrderSummary {
  [status: string]: { count: number; quantity: number; filled: number }
}

export interface FillSummary {
  /** Toplam hacim, lot. */
  quantity: number
  /** Toplam tutar, TL. */
  notional: number
  /** Ağırlıklı ortalama fiyat, TL/MWh. */
  avgPrice: number
  /** Net pozisyon, lot (alış +, satış −). */
  net: number
  avgAdvantage: number
}

export interface LedgerPage<Row, Summary> {
  rows: Row[]
  total: number
  summary: Summary
}

export interface LedgerFilters {
  contract?: string
  status?: string
  side?: string
  since?: string
  until?: string
  search?: string
  sort?: string
  desc?: boolean
  limit?: number
  offset?: number
}

/** Boş/undefined filtreleri atarak sorgu dizesi kurar. */
function sorgu(filters: LedgerFilters): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue
    p.set(k, String(v))
  }
  return p.toString()
}

async function getir<T>(yol: string, filters: LedgerFilters): Promise<T> {
  const response = await fetch(`${yol}?${sorgu(filters)}`)
  if (!response.ok) throw new Error(`Kayıtlar alınamadı (${response.status})`)
  return response.json()
}

// ── Bot Hareketleri ──────────────────────────────────────────────────────────
export interface LedgerBot {
  id: string
  type: string
  contract: string
  params: Record<string, string>
  status: 'RUNNING' | 'PAUSED' | 'REMOVED'
  /** Kontrat kapandı — DB durumu ne derse desin bot fiilen bitmiştir. */
  expired: boolean
  createdAt: number
  updatedAt: number
  orderCount: number
  bought: number
  buyAvg: number
  sold: number
  sellAvg: number
  net: number
}

/** Olay satırı — `ts` ISO string (sunucu ham DB satırı döner). */
export interface BotEvent {
  id: number
  ts: string
  category: string
  action: string
  entityId?: string
  entity_id?: string
  actor: string | null
  severity: string
  message: string | null
  payload: Record<string, unknown>
}

export interface BotReport {
  bot: LedgerBot
  events: BotEvent[]
  orders: {
    id: string
    side: Side
    price: number
    quantity: number
    filled_quantity: number
    status: string
    version: number
    created_at: string
    updated_at: string
  }[]
  fills: {
    order_id: string
    side: Side
    price: number
    quantity: number
    trade_id: string
    trade_price: number
    ts: string
    liquidity: string | null
    best_bid: number | null
    best_ask: number | null
    mcp: number | null
  }[]
}

export async function fetchLedgerBots(contract?: string): Promise<LedgerBot[]> {
  const qs = contract ? `?contract=${encodeURIComponent(contract)}` : ''
  const response = await fetch(`/api/ledger/bots${qs}`)
  if (!response.ok) throw new Error(`Botlar alınamadı (${response.status})`)
  return response.json()
}

// ── Kalıcı silme (onaylı temizlik aracı) ─────────────────────────────────────
async function sil(yol: string, ids: (string | number)[]): Promise<{ deleted: number }> {
  const response = await fetch(yol, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  })
  if (!response.ok) throw new Error(`Silinemedi (${response.status})`)
  return response.json()
}

/** Teklifleri KALICI sil (sürümler + eşleşmeler dahil). Açık emirler sunucuda atlanır. */
export const deleteLedgerOrders = (ids: string[]) => sil('/api/ledger/orders/delete', ids)
/** Eşleşmeleri KALICI sil — net pozisyon ve istatistikler değişir. */
export const deleteLedgerFills = (ids: number[]) => sil('/api/ledger/fills/delete', ids)
/** Bot kayıtlarını TÜM izleriyle KALICI sil (yalnız kaldırılmış botlar). */
export const deleteLedgerBots = (ids: string[]) => sil('/api/ledger/bots/delete', ids)

export async function fetchBotReport(botId: string): Promise<BotReport> {
  const response = await fetch(`/api/ledger/bots/${encodeURIComponent(botId)}`)
  if (!response.ok) throw new Error(`Rapor alınamadı (${response.status})`)
  return response.json()
}

export const fetchLedgerOrders = (f: LedgerFilters) =>
  getir<LedgerPage<LedgerOrder, OrderSummary>>('/api/ledger/orders', f)

export const fetchLedgerFills = (f: LedgerFilters) =>
  getir<LedgerPage<LedgerFill, FillSummary>>('/api/ledger/fills', f)

export async function fetchLedgerContracts(): Promise<string[]> {
  const response = await fetch('/api/ledger/contracts')
  if (!response.ok) return []
  return response.json()
}
